import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { type ConsultationStatus, getConsultation } from '@/api/consultations'
import { ConsultationDetailBody } from '@/components/consultation/consultation-detail-body'
import { Button } from '@/components/ui/button'
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { describeErrorMessage } from '@/lib/errors'

export const Route = createFileRoute('/seller/consultations/$id')({
  component: ConsultationCard,
})

const POLL_INTERVAL_MS = 5_000

function ConsultationCard() {
  const navigate = useNavigate()
  const { id } = Route.useParams()

  const query = useQuery({
    queryKey: ['consultations', 'card', id],
    // Presigned URL подгружается лениво: useQuery не дёргается до первого
    // render'а этого роута (plan §6, T-031).
    queryFn: () => getConsultation(id),
    refetchInterval: (q) => {
      const data = q.state.data
      if (!data) return POLL_INTERVAL_MS
      return isTerminal(data.status) ? false : POLL_INTERVAL_MS
    },
  })

  return (
    <main className="mx-auto flex w-full max-w-xl flex-col gap-4 px-4 py-6 pb-12">
      <Button
        type="button"
        variant="ghost"
        className="-ml-3 w-fit gap-2 text-muted-foreground"
        onClick={() => navigate({ to: '/seller/dashboard' })}
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />К списку записей
      </Button>

      {query.isLoading ? (
        <Card>
          <CardHeader>
            <CardTitle>Загружаем карточку</CardTitle>
          </CardHeader>
        </Card>
      ) : query.isError ? (
        <Card>
          <CardHeader>
            <CardTitle>Не удалось загрузить</CardTitle>
            <CardDescription>
              {describeErrorMessage(query.error, 'Попробуйте обновить страницу.')}
            </CardDescription>
          </CardHeader>
        </Card>
      ) : query.data ? (
        <ConsultationDetailBody card={query.data} onRefresh={() => void query.refetch()} />
      ) : null}
    </main>
  )
}

function isTerminal(status: ConsultationStatus): boolean {
  return status === 'done' || status === 'failed'
}
